import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Tv, Play, RefreshCw } from 'lucide-react';
import { streamFlowApi } from '@lib/streamFlowApi';
import type { FamilyMember, WatchHistoryEntry } from '@lib/streamFlowApi';

/**
 * Watch History — per-member listing of recently watched channels.
 *
 * Reads sf_watch_history directly via Supabase (member-scoped RLS). Each row
 * is an "I watched this channel" event with started_at / ended_at; we display
 * them as cards with a "Watch again" link back into the player. Duration is
 * computed client-side from started_at - ended_at.
 */
export default function WatchHistory() {
  const [members, setMembers] = useState<FamilyMember[]>([]);
  const [memberId, setMemberId] = useState<string | null>(null);
  const [entries, setEntries] = useState<WatchHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load family members once. Default to the first one — the dashboard
  // owns true active-member state; this view just needs SOMEONE selected.
  useEffect(() => {
    streamFlowApi.listMembers().then((res) => {
      if (res.success && res.data) {
        setMembers(res.data);
        if (res.data.length > 0) setMemberId(res.data[0].id);
      } else {
        setError(res.error?.message ?? 'Failed to load family members');
      }
    });
  }, []);

  const refresh = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    const res = await streamFlowApi.listWatchHistory(memberId, 100);
    if (res.success && res.data) {
      setEntries(res.data);
    } else {
      setError(res.error?.message ?? 'Failed to load watch history');
    }
    setLoading(false);
  }, [memberId]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900 mb-3">
        <ArrowLeft className="w-4 h-4" /> Back to dashboard
      </Link>

      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Clock className="w-6 h-6" /> Watch history</h1>
          <p className="text-sm text-gray-500 mt-1">Channels recently watched by {members.find(m => m.id === memberId)?.display_name ?? 'this member'}.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={memberId ?? ''}
            onChange={(e) => setMemberId(e.target.value || null)}
            className="px-3 py-2 border rounded-lg text-sm bg-white"
          >
            {members.map((m) => (
              <option key={m.id} value={m.id}>{m.display_name}</option>
            ))}
          </select>
          <button onClick={refresh} className="p-2 border rounded-lg text-gray-500 hover:bg-gray-50" aria-label="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {error && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading…</div>
      ) : entries.length === 0 ? (
        <div className="text-center py-12 text-gray-500">No watch history yet. Play something to start tracking.</div>
      ) : (
        <ul className="divide-y divide-gray-100 border rounded-lg bg-white">
          {entries.map((entry) => (
            <li key={entry.id} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors">
              <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center flex-shrink-0 overflow-hidden">
                {entry.channel?.logo_url ? (
                  <img src={entry.channel.logo_url} alt="" className="w-full h-full object-contain" />
                ) : (
                  <Tv className="w-5 h-5 text-gray-300" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{entry.channel?.name ?? 'Unknown channel'}</p>
                <p className="text-xs text-gray-500">
                  {new Date(entry.started_at).toLocaleString()}
                  {entry.ended_at && (
                    <>
                      {' · '}
                      {formatDuration(entry.started_at, entry.ended_at)}
                    </>
                  )}
                </p>
              </div>
              <Link
                to={`/watch/${entry.channel_id}`}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border rounded-lg hover:bg-gray-100"
              >
                <Play className="w-3 h-3" /> Watch
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatDuration(startIso: string, endIso: string): string {
  const ms = new Date(endIso).getTime() - new Date(startIso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '';
  const min = Math.round(ms / 60000);
  if (min < 1) return '<1m';
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}
